import { Fragment, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout, { ShellFocus } from './components/Layout';
import SessionGate from './components/SessionGate';
import ConsoleNotices from './components/ConsoleNotices';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TenantList from './pages/TenantList';
import TenantDetail from './pages/TenantDetail';
import UserSearch from './pages/UserSearch';
import UserDetail from './pages/UserDetail';
import AuditLog from './pages/AuditLog';
import AuditDetail from './pages/AuditDetail';

// Statuses in which the session is not proven: no protected screen may be mounted (#583 S5).
const GATED = new Set(['validating', 'covered', 'unavailable', 'signingOut']);

function ProtectedRoute({ children }) {
  const { status, dataRevision } = useAuth();
  const shellFocus = useRef({ revision: null, hadFocus: false });
  if (status === 'active') {
    return (
      <ShellFocus.Provider value={shellFocus.current}>
        <Fragment key={dataRevision}>{children}</Fragment>
      </ShellFocus.Provider>
    );
  }
  if (GATED.has(status)) return <SessionGate />;
  return <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { status } = useAuth();
  if (status === 'active') return <Navigate to="/" replace />;
  if (GATED.has(status)) return <SessionGate />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConsoleNotices />
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tenants" element={<TenantList />} />
            <Route path="/tenants/:id" element={<TenantDetail />} />
            <Route path="/users" element={<UserSearch />} />
            <Route path="/users/:id" element={<UserDetail />} />
            <Route path="/audit" element={<AuditLog />} />
            <Route path="/audit/:id" element={<AuditDetail />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
